import { useForm, useController, FormProvider, useFieldArray } from 'react-hook-form';
import React, { useCallback, useMemo } from 'react';
import { merge, set } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ObjectBrand } from '../../src/shared/types';

import type { FieldPathValue } from 'react-hook-form/dist/types/path/eager';
import type { FieldPath, UseFieldArrayReturn, UseFieldArrayProps, UseFormProps, UseFormReturn, Noop, RefCallBack } from 'react-hook-form/dist/types';
import type { FieldErrors } from 'react-hook-form/dist/types/errors';
import type { TFuncKey } from 'i18next';
import type { Result } from 'neverthrow';
import type { UseControllerProps } from 'react-hook-form/dist/types/controller';
import type { FieldError } from 'react-hook-form';
import type { FieldArrayPath } from 'react-hook-form/dist/types/path';

export interface TypedFormInputProps<Type> {
    field: {
        onChange: (value: Type) => void;
        onBlur: Noop;
        value: Type;
        name: string;
        ref: RefCallBack;
    };
    fieldState: {
        invalid: boolean;
        isTouched: boolean;
        isDirty: boolean;
        error?: FieldError;
    };
}

const IsTypedFormInputSchema = (schema: unknown): schema is TypedFormInputSchema<any, any, any> => {
    return typeof schema === 'object'
        && schema !== null
        && '_rule' in schema
        && typeof schema._rule === 'function';
};

export type TypedFormInputSchema<Inner, Outer extends Inner, Error extends TFuncKey<'common', 'input.error'>> = {
    _innerType: Inner;
    _outerType: Outer;
    _error: Error;
    _rule: (value: Inner) => Result<Outer, Error>;
};

export type ExtendedFormInputSchema<Inner, Outer extends Inner, Error extends TFuncKey<'common', 'input.error'>> = TypedFormInputSchema<Inner, Outer, Error> & { default: (defaultValue: Outer) => TypedFormInputSchema<Inner, Outer, Error> };

export const CreateTypedFormSchema = <Inner, Outer extends Inner, Error extends TFuncKey<'common', 'input.error'>>(props: {
    innerDefaultValue: Inner;
    rule: (value: Inner) => Result<Outer, Error>;
}): ExtendedFormInputSchema<Inner, Outer, Error> => ({
    _innerType: props.innerDefaultValue,
    _rule: props.rule,
    default: (defaultValue: Outer): TypedFormInputSchema<Inner, Outer, Error> => ({
        _innerType: defaultValue,
        _rule: props.rule,
    }) as unknown as TypedFormInputSchema<Inner, Outer, Error>,
}) as ExtendedFormInputSchema<Inner, Outer, Error>;

export type TypedFormSchema = { [key: string]: TypedFormSchema | TypedFormInputSchema<any, any, any> | TypedFormSchema[] };

export type TypedFormInferInnerType<TInternalSchema extends TypedFormSchema> = {
    [key in keyof TInternalSchema]:
    // @ts-expect-error
    undefined extends TInternalSchema[key]['_innerType']
        // @ts-expect-error
        ? TypedFormInferInnerType<TInternalSchema[key]>
        // @ts-expect-error
        : TInternalSchema[key]['_innerType']
};

export type TypedFormInferOuterType<TInternalSchema extends TypedFormSchema> = {
    [key in keyof TInternalSchema]:
    // @ts-expect-error
    undefined extends TInternalSchema[key]['_outerType']
        // @ts-expect-error
        ? TypedFormInferOuterType<TInternalSchema[key]>
        // @ts-expect-error
        : TInternalSchema[key]['_outerType']
};

export const useTypedForm = <
    TSchema extends TypedFormSchema,
    TInnerType extends TypedFormInferInnerType<TSchema> = TypedFormInferInnerType<TSchema>,
>(props: UseFormProps<TypedFormInferInnerType<TSchema>, Record<string, never>> & {
    schema: TSchema;
    initialValues?: TypedFormInferInnerType<TSchema> | null;
    defaultValues?: Partial<TypedFormInferInnerType<TSchema>>;
    onSubmit: (data: TypedFormInferOuterType<TSchema>) => Promise<void>;
    validationBeforeSubmit?: (data: TypedFormInferOuterType<TSchema>) => FieldErrors<TInnerType>;
}): UseFormReturn<TInnerType> & {
    Form: React.FC<{ children: React.ReactNode } & React.FormHTMLAttributes<HTMLFormElement>>;
    Input: <
        TName extends FieldPath<TInnerType> = FieldPath<TInnerType>,
        InputProps extends Record<any, any> = Record<any, any>,
    >(props: {
        name: TName;
        inputProps: InputProps;
        Component: (inputProps: TypedFormInputProps<FieldPathValue<TInnerType, TName>> & InputProps) => React.ReactElement;
    } & UseControllerProps<TInnerType, TName>) => React.ReactElement;
    useFieldArray: <TFieldArrayName extends FieldArrayPath<TInnerType> = FieldArrayPath<TInnerType>, TKeyName extends string = 'id'>(props: Omit<UseFieldArrayProps<TInnerType, TFieldArrayName, TKeyName>, 'control'>) => UseFieldArrayReturn<TInnerType, TFieldArrayName, TKeyName>;
} => {
    const defaultValues = useMemo(() => {
        let resultValues: { [key in keyof TSchema]?: unknown } = {};

        const parseDataAndErrors = (schema: TypedFormSchema, keys: string[]) => {
            Object.entries(schema).forEach(([key, localSchema]) => {
                if (IsTypedFormInputSchema(localSchema)) {
                    set(resultValues, [...keys, key].join('.'), localSchema._innerType);
                    return;
                }

                if (Array.isArray(localSchema)) {
                    set(resultValues, [...keys, key].join('.'), []);
                    return;
                }

                parseDataAndErrors(localSchema, [...keys, key]);
            });
        };

        parseDataAndErrors(props.schema, []);

        if (props.defaultValues !== undefined) {
            resultValues = merge(resultValues, props.defaultValues);
        }

        if (props.initialValues !== undefined && props.initialValues !== null) {
            resultValues = merge(resultValues, props.initialValues);
        }
        return resultValues;
    }, [props.defaultValues, props.initialValues, props.schema]);
    const { t } = useTranslation('common');
    const values = useForm<TInnerType>({
        ...props,
        // @ts-expect-error
        defaultValues,
        // @ts-expect-error
        resolver: (data) => {
            const errors: { [key in keyof TSchema]?: string } = {};
            const resultValues: { [key in keyof TSchema]?: unknown } = {};

            const parseDataAndErrors = (dataValues: Record<string, unknown>, schema: TypedFormSchema, keys: string[]) => {
                Object.entries(schema).forEach(([key, localSchema]) => {
                    if (IsTypedFormInputSchema(localSchema)) {
                        const value = dataValues[key];
                        const result = localSchema._rule(value);

                        if (result.isOk()) {
                            set(resultValues, [...keys, key].join('.'), result.value);
                        } else {
                            set(errors, [...keys, key].join('.'), {
                                message: t(`input.error.${result.error}` as TFuncKey<'common'>),
                            } as FieldError);
                        }

                        return;
                    }

                    const values = dataValues[key];
                    if (Array.isArray(localSchema)) {
                        if (!Array.isArray(values)) {
                            set(errors, [...keys, key].join('.'), {
                                message: 'Value is not Array',
                            } as FieldError);
                            return;
                        }
                        set(resultValues, [...keys, key].join('.'), []);

                        values.forEach((value, index) => {
                            parseDataAndErrors(value, localSchema[0], [...keys, key, `${index}`]);
                        });

                        return;
                    }

                    if (!ObjectBrand.is(values)) {
                        set(errors, [...keys, key].join('.'), {
                            message: 'Value is not Record',
                        } as FieldError);
                        return;
                    }
                    parseDataAndErrors(values, localSchema, [...keys, key]);
                });
            };

            parseDataAndErrors(data, props.schema, []);
            if (Object.keys(errors).length > 0) {
                // eslint-disable-next-line no-console,no-restricted-globals
                console.error(
                    'Form validation: ',
                    errors,
                    data,
                );
            }
            if (Object.keys(errors).length === 0 && props.validationBeforeSubmit !== undefined) {
                //@ts-expect-error
                const validationErrorsResult = props.validationBeforeSubmit(resultValues);
                merge(errors, validationErrorsResult);
            }
            return {
                errors,
                values: Object.keys(errors).length > 0 ? {} : resultValues,
            };
        },
    });
    const Form = useMemo(() => function Form({ children, ...rest }: { children: React.ReactNode } & React.FormHTMLAttributes<HTMLFormElement>) {
        return (
            <FormProvider {...values}>
                <form
                    {...rest}
                    onSubmit={values.handleSubmit(
                        // @ts-expect-error
                        props.onSubmit
                    )}
                >
                    {children}
                </form>
            </FormProvider>
        );
    }, [props.onSubmit, values]);

    const Input = <
        TName extends FieldPath<TInnerType> = FieldPath<TInnerType>,
        InputProps extends Record<any, any> = Record<any, any>,
    >({ name, Component, inputProps }: {
        name: TName;
        inputProps: InputProps;
        Component: (inputProps: TypedFormInputProps<FieldPathValue<TInnerType, TName>> & InputProps) => React.ReactElement;
    } & UseControllerProps<TInnerType, TName>) => {
        const controllerProps = useController({
            control: values.control,
            name,
        });

        return (
            <Component
                {...{
                    ...controllerProps,
                    ...inputProps,
                    setValue: values.setValue,
                }}
            />
        );
    };

    const useFieldArrayHook = useCallback(<TFieldArrayName extends FieldArrayPath<TInnerType> = FieldArrayPath<TInnerType>, TKeyName extends string = 'id'>(props: Omit<UseFieldArrayProps<TInnerType, TFieldArrayName, TKeyName>, 'control'>) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useFieldArray({
            control: values.control,
            ...props,
        });
    }, [values.control]);

    return {
        ...values,
        Form,
        Input,
        useFieldArray: useFieldArrayHook,
    };
};

export type TypedFormInferInputType<TInternalSchema extends TypedFormSchema> = {
    [key in keyof TInternalSchema]:
    // @ts-expect-error
    undefined extends TInternalSchema[key]['_innerType']
        // @ts-expect-error
        ? TypedFormInferInputType<TInternalSchema[key]>
        // @ts-expect-error
        : (inputProps: TypedFormInputProps<TInternalSchema[key]['_innerType']> & { label: string }) => React.ReactElement // TInternalSchema[key]['_innerType']
};
